import { GetItemsResponseContent, PreCheckoutInformation } from "common/types";
import { CatalogItem, ItemStatus, Version as CatalogItemVersion } from "common/types/catalog-types";
import { IOrdersService, OrdersServiceFactory } from "lib/services";
import { CatalogService } from "lib/services/catalog-service";
import { ItemTaxonomyId } from "lib/taxonomy/item-taxonomy-id";
import { IItemVersion, ItemDetails, ItemTypes } from "lib/types/items";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import defaultVersionsData from "./defaultVersions.json";
import { useGetItemDetailsResult } from "./useGetItemDetailsFactory";

export const useLegacyGetSoftwareDetails = (): useGetItemDetailsResult => {
  const history = useHistory();
  const [mappedItem, setMappedItem] = useState<ItemDetails | undefined>(undefined);
  const [defaultVersion, setDefaultVersion] = useState<IItemVersion | undefined>(undefined);
  const [versions, setVersions] = useState<IItemVersion[]>([]);

  const [itemData, setItemData] = useState<CatalogItem | undefined>(undefined);
  const [allItems, setAllItems] = useState<CatalogItem[]>([]);
  const [preCheckoutData, setPreCheckoutData] = useState<PreCheckoutInformation | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const getValue = (value: string | undefined) => value ?? "";

  const fetchItem = async (taxonomyId: ItemTaxonomyId) => {
    if (taxonomyId.dataDomain === "itsoftware") {
      const softwareTxId = taxonomyId.toString().replace("atn::itsoftware", "atn::software");
      taxonomyId = ItemTaxonomyId.Parse(softwareTxId);
    }

    const client = new CatalogService();
    try {
      setIsLoading(true);
      const items: GetItemsResponseContent = await client.GetItems(taxonomyId);
      if (!items.items) {
        setError({ name: "No items", message: "No item data found" });
        throw new Error("No item data found");
      }

      // Filter item requested
      let data = items.items.find((item) => item.itemId === taxonomyId.getTaxonomyId());
      if (!data) {
        data = items.items[0];
      }

      if (!data) {
        setError({ name: "No items", message: "No item data found" });
        setIsLoading(false);
        history.push("/");
      }

      setError(undefined);
      setPreCheckoutData(undefined);
      setAllItems(items.items || []);
      setItemData(data);
    } catch (error) {
      setError(error as Error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (itemData) {
      setHookInfo();
    }
  }, [itemData]);

  const setHookInfo = () => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    let defaultVersionToSelect: CatalogItemVersion = itemData?.versions![0]!;

    let orderedVersions: CatalogItemVersion[] = [];

    // Sort versions alphabetically by shortDescription

    // Only children versions have property "name"
    if (itemData?.versions && itemData.versions[0].name) {
      orderedVersions = _.orderBy(itemData.versions, [(item) => item.shortDescription?.toLowerCase()], ["asc"]);
    }
    // Version is not a child of the product, use main item taxonomy id
    else {
      defaultVersionToSelect.id = itemData?.itemId;
    }

    const defaultVersionInfo = defaultVersionsData.defaultVersions.find((entry) => entry.itemId === itemData?.itemId);
    // Find if there is a default version and move it to the first position
    if (orderedVersions && defaultVersionInfo) {
      const defaultVersionId = defaultVersionInfo.defaultVersion.id;
      const defaultVersionIndex = orderedVersions.findIndex((version) => version.id === defaultVersionId);

      // Choose version for dropdown
      if (orderedVersions[defaultVersionIndex]) {
        defaultVersionToSelect = orderedVersions[defaultVersionIndex];
      }

      if (defaultVersionIndex > 0) {
        const [defaultVersion] = orderedVersions.splice(defaultVersionIndex, 1);
        orderedVersions.unshift(defaultVersion);
      }
    }

    // Use first item as default version
    setDefaultVersion(mapVersion(defaultVersionToSelect, allItems));

    // Set available versions
    setVersions(mapVersions(orderedVersions, allItems));

    // Set mapped item info
    setMappedItem(mapItem(itemData!, defaultVersionToSelect));

    setIsLoading(false);
  };

  /* istanbul ignore next */
  const fetchPreCheckoutData = async (token: string, isOsCompatible = true) => {
    try {
      const ordersService: IOrdersService = OrdersServiceFactory.getInstance(mappedItem?.taxonomyId ?? "");
      const checkoutData = await ordersService.GetPreCheckoutInformation(
        mappedItem?.taxonomyId ?? "",
        token,
        isOsCompatible
      );
      if (!checkoutData.information) {
        throw new Error("Couldn't obtain pre checkout information");
      }
      setError(undefined);
      setPreCheckoutData(checkoutData.information);
    } catch (error) {
      setError(error as Error);
    }
  };

  const mapVersions = (versions?: CatalogItemVersion[], originalItems?: CatalogItem[]): IItemVersion[] => {
    if (!versions || versions.length === 0) {
      return [];
    }

    return versions.map((catalogVersion) => mapVersion(catalogVersion, originalItems)!);
  };

  const mapVersion = (
    version: CatalogItemVersion | undefined,
    originalItems?: CatalogItem[]
  ): IItemVersion | undefined => {
    if (!version) {
      return undefined;
    }

    // Find the corresponding catalog item for this version to get the orderRedirectURL
    const correspondingItem = originalItems?.find((item) => item.itemId === version.id);

    return {
      taxonomyId: getValue(version?.id),
      name: getValue(version.name),
      displayText: getValue(version.shortDescription),
      status: version?.status || ItemStatus.unavailable,
      amount: version?.price || 0,
      currency: getValue(version?.currency),
      applinkInstanceName: getValue(version?.applinkInstanceName),
      applinkProductName: getValue(version?.applinkProductName),
      isReturnable: version?.isReturnable,
      orderRedirectUrl: correspondingItem?.references?.orderRedirectURL,
      acme: {
        isEnabled: version?.acme?.isEnabled ?? false,
        windowsDeepLinkUrl: getValue(version?.acme?.windowsDeepLinkUrl || undefined),
        macOsDeepLinkUrl: getValue(version?.acme?.macOsDeepLinkUrl || undefined),
      },
    };
  };

  const updateVersionSelected = (selectedVersion: IItemVersion) => {
    if (itemData?.versions && itemData.versions.length > 0) {
      if (mappedItem && selectedVersion.displayText) {
        // Keeping existing logic for Software versions, only overrides item taxonomy id.
        const overrideId = { ...mappedItem, taxonomyId: selectedVersion.taxonomyId };
        const newCurrentVersion = itemData.versions.find(
          (IItemVersion) => IItemVersion.id === selectedVersion.taxonomyId
        );

        // update applinkinstance and productname
        if (newCurrentVersion) {
          overrideId.applinkInstanceName = getValue(newCurrentVersion.applinkInstanceName);
          overrideId.applinkProductName = getValue(newCurrentVersion.applinkProductName);
          overrideId.isReturnable = newCurrentVersion.isReturnable;
        }

        setDefaultVersion(mapVersion(newCurrentVersion, allItems));
        setMappedItem(overrideId);
      }
    }
  };

  const mapItem = (item: CatalogItem, defaultVersion: CatalogItemVersion): ItemDetails => {
    return {
      taxonomyId: getValue(defaultVersion.id), // Use taxonomy id of the default version
      name: getValue(item.name),
      description: getValue(item.description),
      shortDescription: getValue(item.shortDescription),
      status: getValue(item.status),
      logoUrl: getValue(item.logoImageUrl),
      publisher: getValue(item.manufacturer),
      etaDays: item.etaDays,
      os: item.os ?? [],
      categories: item.tags ?? [],
      hasStock: item.hasStock,
      orderRedirectUrl: item.references?.orderRedirectURL,
      moreDetailsUrl: item.references?.statusMoreDetailsURL,
      orderWorkflow: item.orderWorkflow,
      externalButtonText: item.externalButtonText,
      priceAmount: defaultVersion.price ?? 0,
      priceCurrency: defaultVersion.currency ?? "",
      requiresLicenseCapValidation: item.requiresLicenseCapValidation,
      applinkProductName: getValue(item.applinkProductName),
      applinkInstanceName: getValue(item.applinkInstanceName),
      isReturnable: item.isReturnable,
      subtype: item.subtype,
      acme: {
        isEnabled: item.acme?.isEnabled ?? false,
        windowsDeepLinkUrl: item.acme?.windowsDeepLinkUrl,
        macOsDeepLinkUrl: item.acme?.macOsDeepLinkUrl,
      },
    };
  };

  return {
    type: ItemTypes.SOFTWARE,
    item: mappedItem,
    isLoading,
    error,
    preCheckoutData,
    defaultVersion,
    versions,
    updateVersionSelected,
    fetchPreCheckoutData,
    getItem: fetchItem,
  };
};
